import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer, Box, List, ListItemButton, ListItemIcon, ListItemText,
  Typography, Divider, Collapse
} from '@mui/material';
import {
  Dashboard, AccountBalance, Receipt, CurrencyExchange,
  SendToMobile, AssignmentReturn, Group, BarChart, History, Summarize, Verified,
  ManageHistory, ShoppingCart, TravelExplore, Inventory, ExpandLess, ExpandMore,
  Search as SearchIcon, LocalShipping, Storefront
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

export const DRAWER_WIDTH = 250;

const ALL_NAV_ITEMS = [
  { label: 'Dashboard',           path: '/',              icon: <Dashboard />,        module: 'dashboard' },
  { label: 'Beneficiaries',       path: '/beneficiaries', icon: <AccountBalance />,   module: 'beneficiaries' },
  { label: 'Proforma Invoices',   path: '/invoices',      icon: <Receipt />,          module: 'invoices' },
  { label: 'Purchase Orders',     path: '/purchase-orders', icon: <Receipt />,        module: 'invoices' },
  { label: 'Shipping Invoices',   path: '/shipping-invoices', icon: <LocalShipping />, module: 'invoices' },
  { label: 'Outward Remittances', path: '/remittances',   icon: <SendToMobile />,     module: 'remittances' },
  { label: 'BOE Knock-off',       path: '/boe',              icon: <AssignmentReturn />, module: 'boe' },
  { label: 'Historical BOE',      path: '/historical-boe',   icon: <History />,          module: 'historical_boe' },
  { label: 'Reports',             path: '/reports',          icon: <BarChart />,         module: 'reports' },
  { label: 'Financial Reports',   path: '/reports2',         icon: <Summarize />,        module: 'reports' },
  { label: 'Certifications',      path: '/certifications',   icon: <Verified />,         module: 'certifications' },
];
const SOURCING_ITEMS = [
  { label: 'Product Searches',    path: '/sourcing/searches', icon: <SearchIcon />,     module: 'sourcing',   alwaysVisible: true },
  { label: 'Supplier Discovery',  path: '/sourcing/discover', icon: <TravelExplore />,  module: 'sourcing',   alwaysVisible: true },
  { label: 'Orders to Place',     path: '/sourcing/orders',   icon: <ShoppingCart />,   module: 'sourcing',   alwaysVisible: true },
  { label: 'Exhibition Finds',    path: '/sourcing/exhibition', icon: <Storefront />,   module: 'sourcing',   alwaysVisible: true },
];
const ADMIN_ITEMS = [
  { label: 'User Management', path: '/users',        icon: <Group />,         module: 'users' },
  { label: 'Activity Log',    path: '/activity-log', icon: <ManageHistory />, module: 'activity_log', alwaysVisible: true },
];

function NavItem({ item, isActive, onNavigate }) {
  return (
    <ListItemButton
      onClick={() => onNavigate(item.path)}
      sx={{
        mx: 1.25, mb: 0.25, borderRadius: 2, py: 0.85, px: 1.5,
        bgcolor: isActive ? 'rgba(255,255,255,0.12)' : 'transparent',
        color: isActive ? '#fff' : 'rgba(255,255,255,0.72)',
        '&:hover': { bgcolor: 'rgba(255,255,255,0.07)', color: '#fff' },
        transition: 'background-color 0.2s, color 0.2s',
      }}
    >
      <ListItemIcon sx={{ color: 'inherit', minWidth: 32, '& svg': { fontSize: 20 } }}>{item.icon}</ListItemIcon>
      <ListItemText
        primary={item.label}
        primaryTypographyProps={{ fontSize: 13.5, fontWeight: isActive ? 600 : 500 }}
      />
    </ListItemButton>
  );
}

function SidebarContent({ onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { canAccess } = useAuth();
  const [sourcingOpen, setSourcingOpen] = useState(location.pathname.startsWith('/sourcing'));

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  // Navigate and auto-close mobile drawer
  const handleNavigate = (path) => {
    navigate(path);
    if (onClose) onClose();
  };

  const visibleNav      = ALL_NAV_ITEMS.filter(item => canAccess(item.module));
  const visibleSourcing = SOURCING_ITEMS.filter(item => item.alwaysVisible || canAccess(item.module));
  const visibleAdmin    = ADMIN_ITEMS.filter(item => item.alwaysVisible || canAccess(item.module));

  return (
    <Box sx={{
      height: '100%',
      bgcolor: '#1a1a2e',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Logo */}
      <Box sx={{ p: 2.5, pb: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box sx={{
          width: 130, height: 50, bgcolor: 'white', borderRadius: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          mb: 1.25, overflow: 'hidden', p: 0.75,
        }}>
          <img
            src={`${process.env.PUBLIC_URL}/identium-logo.jpeg`}
            alt="Identium"
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
          />
          <Typography sx={{ display: 'none', fontWeight: 800, color: '#1a3adb', fontSize: 14 }}>
            IDENTIUM
          </Typography>
        </Box>
        <Typography sx={{ color: 'rgba(255,255,255,0.55)', letterSpacing: 1.5, textTransform: 'uppercase', fontSize: 10, fontWeight: 600 }}>
          FX · Payment Manager
        </Typography>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mx: 2 }} />

      <List sx={{ flex: 1, py: 1.5, px: 0.5, overflowY: 'auto' }}>
        <Typography variant="caption" sx={{ px: 2.5, color: 'rgba(255,255,255,0.45)', fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', fontSize: 10.5 }}>
          Main Menu
        </Typography>
        <Box sx={{ mt: 1 }}>
          {visibleNav.map(item => (
            <NavItem key={item.path} item={item} isActive={isActive(item.path)} onNavigate={handleNavigate} />
          ))}
        </Box>

        {visibleSourcing.length > 0 && (
          <>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 2, mx: 2 }} />
            <Typography sx={{ px: 2.5, mb: 0.5, color: 'rgba(255,255,255,0.45)', fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', fontSize: 10.5 }}>
              Sourcing
            </Typography>
            <ListItemButton
              onClick={() => setSourcingOpen(o => !o)}
              sx={{
                mx: 1.25, mb: 0.25, borderRadius: 2, py: 0.85, px: 1.5,
                color: 'rgba(255,255,255,0.72)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.07)', color: '#fff' },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 32, '& svg': { fontSize: 20 } }}><Inventory /></ListItemIcon>
              <ListItemText primary="Browse"
                primaryTypographyProps={{ fontSize: 13.5, fontWeight: 500 }} />
              {sourcingOpen ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
            </ListItemButton>
            <Collapse in={sourcingOpen} timeout="auto" unmountOnExit>
              <Box sx={{ pl: 1.5 }}>
                {visibleSourcing.map(item => (
                  <NavItem key={item.path} item={item} isActive={isActive(item.path)} onNavigate={handleNavigate} />
                ))}
              </Box>
            </Collapse>
          </>
        )}

        {visibleAdmin.length > 0 && (
          <>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 2, mx: 2 }} />
            <Typography variant="caption" sx={{ px: 2.5, color: 'rgba(255,255,255,0.45)', fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', fontSize: 10.5 }}>
              Administration
            </Typography>
            <Box sx={{ mt: 1 }}>
              {visibleAdmin.map(item => (
                <NavItem key={item.path} item={item} isActive={isActive(item.path)} onNavigate={handleNavigate} />
              ))}
            </Box>
          </>
        )}
      </List>

      <Box sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10.5, fontWeight: 500 }}>
          Identium Tech Solutions Pvt Ltd
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: 10, mt: 0.25 }}>
          v1.0 · Import FX System
        </Typography>
      </Box>
    </Box>
  );
}

export default function Sidebar({ mobileOpen, onClose }) {
  return (
    <>
      {/* Mobile drawer — passes onClose so nav items auto-collapse it */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box', border: 'none' },
        }}
      >
        <SidebarContent onClose={onClose} />
      </Drawer>

      {/* Desktop permanent drawer — no onClose needed */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box', border: 'none' },
        }}
        open
      >
        <SidebarContent />
      </Drawer>
    </>
  );
}
